import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useCurrentUser } from './useCurrentUser';
import usePagina from './usePagina';
import useProductCategories from './useProductCategories';
import { useToast } from '../context/ToastContext';
import ProductService, { CHAVES_PRODUTOS } from '../api/ProductService';
import FileStorageService from '../api/FileStorageService';
import { compressImage } from '../utils/imageOptimization';
import { getApiErrorMessage } from '../utils/http';
import { UserProduct } from '../types/entities';
import { ExpiryStatus } from '../types/enums';
import { formatDate as formatDateBase } from '../utils/date';

/** Data no estilo curto (ex.: 25 de dez. de 2026). Fonte: utils/date. */
export const formatDate = (value: string | null | undefined): string =>
  formatDateBase(value, 'short');

interface ProductForm {
  name: string;
  brand: string;
  imageUrl: string;
  productCategoryId: string;
  openedAt: string;
  expiresAt: string;
  notes: string;
  /** Digitados separados por vírgula; viram lista só no envio. */
  mainActives: string;
  benefits: string;
}

const EMPTY_FORM: ProductForm = {
  name: '',
  brand: '',
  imageUrl: '',
  productCategoryId: '',
  openedAt: '',
  expiresAt: '',
  notes: '',
  mainActives: '',
  benefits: '',
};

const paraLista = (texto: string): string[] =>
  texto.split(',').map((t) => t.trim()).filter(Boolean);

export default function useProducts() {
  const { userId } = useCurrentUser();
  const { categories } = useProductCategories();
  const toast = useToast();

  const [form, setForm] = useState<ProductForm>({ ...EMPTY_FORM });
  const [saving, setSaving] = useState<boolean>(false);
  const [enviandoFoto, setEnviandoFoto] = useState<boolean>(false);
  // 0..100 enquanto os bytes sobem; null fora do envio.
  const [progressoFoto, setProgressoFoto] = useState<number | null>(null);
  const [error, setError] = useState<string>('');
  const [editingProduct, setEditingProduct] = useState<UserProduct | null>(null);
  const [deletingProduct, setDeletingProduct] = useState<UserProduct | null>(null);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [filterStatus, setFilterStatus] = useState<ExpiryStatus | 'ALL'>('ALL');

  // A página já vem filtrada pelo backend; a contagem é da prateleira inteira.
  const prateleira = usePagina(
    CHAVES_PRODUTOS.lista(userId!, filterStatus),
    (page) => ProductService.getPage(userId!, page, filterStatus === 'ALL' ? undefined : filterStatus),
    Boolean(userId),
  );

  const contagemQuery = useQuery({
    queryKey: CHAVES_PRODUTOS.contagem(userId!),
    queryFn: async () => (await ProductService.getCounts(userId!)).data,
    enabled: Boolean(userId),
  });

  const contagem = contagemQuery.data;
  const totalProdutos = contagem ? Object.values(contagem).reduce((soma, n) => soma + n, 0) : 0;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  /** A foto vai para o storage na hora da escolha: o formulário guarda a URL, não o arquivo. */
  const enviarFoto = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      setError('Escolha um arquivo de imagem.');
      return;
    }
    setEnviandoFoto(true);
    setProgressoFoto(0);
    setError('');
    try {
      const comprimida = await compressImage(file, {
        maxWidth: 800,
        maxHeight: 800,
        quality: 0.8,
        format: 'webp',
      });
      const { url } = await FileStorageService.uploadProductImage(comprimida, setProgressoFoto);
      setForm((prev) => ({ ...prev, imageUrl: url }));
    } catch (err: unknown) {
      const msg = getApiErrorMessage(err, 'Não foi possível enviar a foto.');
      setError(msg);
      toast.erro(msg);
    } finally {
      setEnviandoFoto(false);
      setProgressoFoto(null);
    }
  };

  const removerFoto = () => setForm((prev) => ({ ...prev, imageUrl: '' }));

  const resetForm = () => {
    setForm({ ...EMPTY_FORM });
    setEditingProduct(null);
    setShowForm(false);
    setError('');
  };

  const startEdit = (product: UserProduct) => {
    setEditingProduct(product);
    setForm({
      name: product.name ?? '',
      brand: product.brand ?? '',
      imageUrl: product.imageUrl ?? '',
      productCategoryId: product.productCategoryId ?? '',
      openedAt: product.openedAt ?? '',
      expiresAt: product.expiresAt ?? '',
      notes: product.notes ?? '',
      mainActives: (product.mainActives ?? []).join(', '),
      benefits: product.benefits ?? '',
    });
    setShowForm(true);
  };

  // Sem useMutation (não há atualização otimista): o create/update/remove do
  // service já invalida `produtos` e a lista se refaz sozinha.
  const saveProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId) return;
    setSaving(true);
    setError('');

    const payload = {
      ...form,
      mainActives: paraLista(form.mainActives),
    };

    try {
      if (editingProduct?.id) {
        await ProductService.update(userId, editingProduct.id, payload);
        toast.sucesso('Produto atualizado.');
      } else {
        await ProductService.create(userId, payload);
        toast.sucesso('Produto adicionado à sua prateleira.');
      }
      resetForm();
    } catch (err: unknown) {
      const msg = getApiErrorMessage(err, 'Erro ao salvar o produto. Tente novamente.');
      setError(msg);
      toast.erro(msg);
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!userId || !deletingProduct?.id) return;
    try {
      await ProductService.remove(userId, deletingProduct.id);
      setDeletingProduct(null);
      toast.sucesso('Produto excluído.');
    } catch (err: unknown) {
      const msg = getApiErrorMessage(err, 'Erro ao excluir o produto. Tente novamente.');
      setError(msg);
      toast.erro(msg);
    }
  };

  return {
    products: prateleira.itens,
    totalProdutos,
    vencidos: contagem?.[ExpiryStatus.EXPIRED] ?? 0,
    vencendo: contagem?.[ExpiryStatus.EXPIRING_SOON] ?? 0,
    pagina: prateleira.pagina,
    totalPaginas: prateleira.totalPaginas,
    setPagina: prateleira.setPagina,
    categories,
    form,
    loading: prateleira.query.isLoading || contagemQuery.isLoading,
    saving,
    enviandoFoto,
    progressoFoto,
    error: error || (prateleira.query.isError ? 'Não foi possível carregar os produtos.' : ''),
    editingProduct,
    deletingProduct,
    setDeletingProduct,
    showForm,
    setShowForm,
    filterStatus,
    setFilterStatus,
    handleChange,
    enviarFoto,
    removerFoto,
    resetForm,
    startEdit,
    saveProduct,
    confirmDelete,
    formatDate,
  };
}
